import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import {
  emailRegex,
  INK,
  MINT_BG,
  MUTED,
  TEAL,
  TEAL_DARK,
  theme,
} from "./helper";
import postsServices from "../../../services/api/service/postsService";
import { useSelector } from "react-redux";
import { RootState } from "../../../services/redux/store";
import service from "../../../services/api/service";
import Toast from "../../../components/ui/Toast";
import AwsBgImg from "../../../assets/aws/aws_bg.png";
import AwsBgMobileImg from "../../../assets/aws/aws_bg_mobile.jpeg";

// ---------------- Component ----------------
const MIN_MOBILE_DIGITS = 7;
const MAX_MOBILE_DIGITS = 15;

const normalizeMobileValue = (value: string) => value.replace(/\D/g, "");

interface PhoneCode {
  id: number;
  country: string;
  countryCode: string;
}

const withPlusPrefix = (code: unknown) => {
  const value = String(code ?? "").trim();
  if (!value) return "";
  return value.startsWith("+") ? value : `+${value}`;
};

const isPlainNumericCode = (code: unknown) =>
  /^\d+$/.test(
    String(code ?? "")
      .trim()
      .replace(/^\+/, ""),
  );

const codeDigits = (code: unknown) => {
  const digits = String(code ?? "").replace(/\D/g, "");
  return digits ? Number(digits) : null;
};

interface FormState {
  country: number | null;
  mobile: string;
  email: string;
  consent: boolean;
}

interface Errors {
  mobile?: string;
  email?: string;
  country?: string;
  consent?: string;
}

interface Props {
  isAwsOfferModalOpen: boolean;
  setIsAwsOfferModalOpen: (val: boolean) => void;
}

export default function AwsOfferModal({
  isAwsOfferModalOpen,
  setIsAwsOfferModalOpen,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [values, setValues] = useState<FormState>({
    country: null,
    mobile: "",
    email: "",
    consent: false,
  });
  const [errors, setErrors] = useState<Errors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [phoneCodes, setPhoneCodes] = useState<PhoneCode[]>([]);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const selectedLoanNumber = useSelector(
    (state: RootState) => state?.dashboardData?.selectedLoanNumber ?? "",
  );

  const countryOptions = useMemo<PhoneCode[]>(() => {
    if (!Array.isArray(phoneCodes)) return [];

    const uniqueCodes = phoneCodes.filter(
      (pc, index, self) =>
        index === self.findIndex((p) => p?.countryCode === pc?.countryCode),
    );

    return uniqueCodes.sort((a, b) => {
      const aPlain = isPlainNumericCode(a?.countryCode);
      const bPlain = isPlainNumericCode(b?.countryCode);
      if (aPlain !== bPlain) return aPlain ? -1 : 1;

      const aDigits = codeDigits(a?.countryCode);
      const bDigits = codeDigits(b?.countryCode);

      if (aDigits !== null && bDigits !== null) {
        if (aDigits !== bDigits) return aDigits - bDigits;
      } else if (aDigits === null && bDigits !== null) {
        return 1;
      } else if (aDigits !== null && bDigits === null) {
        return -1;
      }

      return String(a?.countryCode ?? "").localeCompare(
        String(b?.countryCode ?? ""),
      );
    });
  }, [phoneCodes]);

  const selectedCountryOption = useMemo<PhoneCode | null>(
    () => countryOptions.find((c) => c.id === values.country) ?? null,
    [countryOptions, values.country],
  );

  const validateField = (
    name: keyof FormState,
    next: FormState,
  ): string | undefined => {
    if (name === "country") {
      if (next.country === null || next.country === undefined)
        return "Country code is required";
    }

    if (name === "mobile") {
      const normalizedMobile = normalizeMobileValue(next.mobile);

      if (!normalizedMobile.trim()) return "Mobile number is required";
      if (!/^\d+$/.test(normalizedMobile)) return "Digits only";
      if (
        normalizedMobile.length < MIN_MOBILE_DIGITS ||
        normalizedMobile.length > MAX_MOBILE_DIGITS
      ) {
        return `Mobile number must be between ${MIN_MOBILE_DIGITS} and ${MAX_MOBILE_DIGITS} digits`;
      }
    }
    if (name === "email") {
      if (!next.email.trim()) return "Email is required";
      if (!emailRegex.test(next.email)) return "Enter a valid email";
    }

    if (name === "consent") {
      if (!next.consent) return "Consent is required to proceed";
    }
    return undefined;
  };

  const runValidation = (state: FormState): Errors => {
    const e: Errors = {};
    (["mobile", "email", "consent", "country"] as const).forEach((k) => {
      const msg = validateField(k, state);
      if (msg) e[k] = msg;
    });
    return e;
  };

  const setField = <K extends keyof FormState>(
    name: K,
    value: FormState[K],
  ) => {
    const next = { ...values, [name]: value };
    setValues(next);
    if (touched[name]) {
      const msg = validateField(name, next);
      setErrors((prev) => ({ ...prev, [name]: msg }));
    }
    // Re-validate mobile when country changes
    if (name === "country" && touched.mobile) {
      const mMsg = validateField("mobile", next);
      setErrors((prev) => ({ ...prev, mobile: mMsg }));
    }
  };

  const markTouched = (name: keyof FormState) => {
    setTouched((p) => ({ ...p, [name]: true }));
    const msg = validateField(name, values);
    setErrors((prev) => ({ ...prev, [name]: msg }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const all = runValidation(values);
    setErrors(all);
    setTouched({ country: true, mobile: true, email: true, consent: true });
    if (Object.keys(all).length > 0) {
      setToast({
        open: true,
        message: "Please fix the highlighted errors before submitting.",
        severity: "error",
      });
      return;
    }
    setSubmitting(true);
    const payload: any = {
      emailId: values?.email,
      phone: values?.mobile,
      countryCodeId: Number(values?.country),
      lanNumber: selectedLoanNumber,
    };
    service.posts
      .addPost(`api/v1/customer/referral`, payload)
      .then((response: any) => {
        setToast({
          open: true,
          message:
            response?.data?.message ||
            "Thank you for sharing your details! Your Avanse representative will get in touch with you shortly.",
          severity: "success",
        });
        setTimeout(() => {
          setSubmitting(false);
          setIsAwsOfferModalOpen(false);
        }, 2000);
      })
      .catch((error) => {
        console.log("error", error);
        setToast({
          open: true,
          message:
            error?.data?.message ||
            "Oops! Something went wrong on our end. Please try again in a few minutes.",
          severity: "error",
        });
        setSubmitting(false);
      });

    setTouched({});
    setErrors({});
  };

  const fetchPhoneCodes = async () => {
    try {
      const response = await postsServices.addPost(
        "/api/v1/disbursement-form/default",
        {
          loanAccountNumber: selectedLoanNumber,
        },
      );
      if (response && response?.status === 200) {
        setPhoneCodes(response?.data?.data?.getDefaultData?.PhoneCodes);
      }
    } catch (err) {
      console.log("Error while fetching phone codes:", err);
    }
  };

  useEffect(() => {
    fetchPhoneCodes();
  }, []);

  return (
    <Dialog
      open={isAwsOfferModalOpen}
      onClose={() => setIsAwsOfferModalOpen(false)}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: "8px",
          maxWidth: "1080px",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <ThemeProvider theme={theme}>
          <Toast
            open={toast.open}
            message={toast.message}
            severity={toast.severity}
            onClose={() => setToast({ ...toast, open: false })}
          />
          <Box
            data-testid="aws-offer-page"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: theme.typography.fontFamily,
              backgroundImage: `url(${isMobile ? AwsBgMobileImg : AwsBgImg})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundSize: { xs: "100% 100%", sm: "cover" },
            }}
          >
            <Box
              data-testid="aws-offer-card"
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: 1180,
                overflow: "hidden",
                px: { xs: 1.5, sm: 5, md: 8 },
                py: { xs: 5, sm: 6, md: 7 },
              }}
            >
              {/* Heading */}
              <Typography
                data-testid="aws-offer-title"
                component="h1"
                sx={{
                  color: TEAL_DARK,
                  fontWeight: 700,
                  textAlign: { xs: "left", sm: "center" },
                  fontSize: { xs: 16, sm: 22 },
                  mb: { xs: 2, sm: 5, md: 6 },
                  px: { xs: 0, sm: 6 },
                  lineHeight: 1.3,
                }}
              >
                Please fill in your details below to know more about the AWS
                offer.
              </Typography>

              {/* Form */}
              <Box
                component="form"
                noValidate
                onSubmit={handleSubmit}
                data-testid="aws-offer-form"
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={{ xs: 2.5, md: 3 }}
                  sx={{ mb: 3 }}
                >
                  {/* Mobile with country selector */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "stretch",
                          bgcolor: "#fff",
                          border: `1.5px solid ${
                            errors.mobile && touched.mobile ? "#d43a3a" : TEAL
                          }`,
                          borderRadius: "10px",
                          overflow: "hidden",
                          "&:focus-within": {
                            borderColor:
                              errors.mobile && touched.mobile
                                ? "#d43a3a"
                                : TEAL_DARK,
                            borderWidth: 2,
                          },
                        }}
                      >
                        <Autocomplete<PhoneCode, false, false, false>
                          data-testid="country-select"
                          options={countryOptions}
                          value={selectedCountryOption}
                          onChange={(_event, option) =>
                            setField("country", option ? option.id : null)
                          }
                          // Backend value, shown with a leading "+".
                          getOptionLabel={(option) =>
                            withPlusPrefix(option?.countryCode)
                          }
                          isOptionEqualToValue={(option, selected) =>
                            option?.id === selected?.id
                          }
                          autoHighlight
                          selectOnFocus
                          handleHomeEndKeys
                          openOnFocus
                          clearOnEscape
                          clearText="Clear country code"
                          noOptionsText="No matching code"
                          slotProps={{
                            paper: {
                              "data-testid": "country-menu",
                              sx: { minWidth: 80 },
                            } as any,
                            popper: {
                              sx: { width: "auto !important", minWidth: 110 },
                            },
                          }}
                          renderOption={(props, option) => (
                            <li
                              {...props}
                              key={option.id}
                              data-testid={`country-option-${option.id}`}
                            >
                              <span style={{ color: MUTED }}>
                                {withPlusPrefix(option.countryCode)}
                              </span>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              placeholder=""
                              InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                              }}
                              inputProps={{
                                ...params.inputProps,
                                "aria-label": "Country code",
                              }}
                            />
                          )}
                          sx={{
                            minWidth: { xs: 76, sm: 96 },
                            borderRight: `1px solid ${TEAL}55`,
                            display: "flex",
                            alignItems: "center",
                           
                            "& .MuiAutocomplete-clearIndicator": {
                              p: 0.25,
                              mr: 0,
                              visibility: "hidden",
                            },
                            "&:hover .MuiAutocomplete-clearIndicator, &:focus-within .MuiAutocomplete-clearIndicator":
                              {
                                visibility: "visible",
                              },
                            "& .MuiAutocomplete-popupIndicator": {
                              p: 0.25,
                              mr: 0,
                            },
                            "& .MuiAutocomplete-endAdornment svg": {
                              fontSize: 16,
                            },
                        
                            "&.MuiAutocomplete-root .MuiAutocomplete-inputRoot": {
                              paddingRight: "0px !important",
                            },
                            "& .MuiInputBase-root": {
                              height: "100%",
                              pl: 1.5,
                            },
                            "& .MuiInputBase-input": {
                              py: 0,
                              fontSize: 14,
                              fontWeight: 400,
                              color: "#000",
                            },
                          }}
                        />
                        <input
                          data-testid="mobile-input"
                          value={values.mobile}
                          onChange={(e) => {
                            const digits = normalizeMobileValue(e.target.value);
                            setField(
                              "mobile",
                              digits.slice(0, MAX_MOBILE_DIGITS),
                            );
                          }}
                          onBlur={() => markTouched("mobile")}
                          placeholder="Mobile Number*"
                          inputMode="numeric"
                          maxLength={MAX_MOBILE_DIGITS}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            border: "none",
                            outline: "none",
                            borderRadius: "10px",
                            padding: "16.5px 16px",
                            fontSize: 15,
                            color: "#000",
                            backgroundColor: "transparent",
                            fontFamily: "jost",
                            boxSizing: "border-box",
                          }}
                        />
                      </Box>
                      {errors.mobile && touched.mobile && (
                        <Typography
                          data-testid="mobile-error"
                          sx={{
                            color: "#d43a3a",
                            fontSize: 12.5,
                            mt: 0.75,
                            ml: 1,
                          }}
                        >
                          {errors.mobile}
                        </Typography>
                      )}
                      {errors.country && touched.country && (
                        <Typography
                          data-testid="country-error"
                          sx={{ color: "#d43a3a", fontSize: 12.5, ml: 1 }}
                        >
                          {errors.country}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Email */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      data-testid="email-input"
                      fullWidth
                      label="E-Mail ID*"
                      value={values.email}
                      onChange={(e) => setField("email", e.target.value)}
                      onBlur={() => markTouched("email")}
                      error={Boolean(errors.email && touched.email)}
                      helperText={touched.email ? errors.email || "" : ""}
                      autoComplete="email"
                      sx={{
                        borderRadius: "10px",
                        fontSize: 15,
                        "& .MuiInputLabel-root": {
                          fontSize: 15,
                          color: "#818181",
                        },
                        "& .MuiFormHelperText-root": {
                          marginTop: "6px",
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          fontSize: 15,
                          color: "#000",
                          "& fieldset": {
                            borderColor: TEAL,
                          },
                          "&:hover fieldset": {
                            borderColor: TEAL_DARK,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: TEAL_DARK,
                            borderWidth: 2,
                          },
                        },
                      }}
                    />
                  </Box>

                  {/* LAN */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      data-testid="lan-input"
                      fullWidth
                      label="LAN*"
                      value={selectedLoanNumber || ""}
                      disabled
                      sx={{
                        borderRadius: "10px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          "& fieldset": {
                            borderColor: TEAL,
                          },
                          "& .Mui-disabled": {
                            fontSize: 14,
                          },
                        },
                      }}
                    />
                  </Box>
                </Stack>

                {/* Consent */}
                <Box sx={{ mb: { xs: 1, sm: 4 } }}>
                  <FormControlLabel
                    sx={{
                      alignItems: "flex-start",
                      ml: 0,
                      mr: 0,
                      "& .MuiFormControlLabel-label": {
                        fontSize: { xs: 12.5, sm: 13.5 },
                        color: INK,
                        lineHeight: 1.55,
                      },
                    }}
                    control={
                      <Checkbox
                        data-testid="consent-checkbox"
                        checked={values.consent}
                        onChange={(e) => {
                          setField("consent", e.target.checked);
                          setTouched((p) => ({ ...p, consent: true }));
                        }}
                        sx={{
                          pt: 0,
                          color:
                            errors.consent && touched.consent
                              ? "#d43a3a"
                              : TEAL,
                          "&.Mui-checked": { color: TEAL },
                        }}
                      />
                    }
                    label={
                      <Box
                        sx={{
                          color: "#000000",
                          fontWeight: 300,
                          fontFamily: "Jost",
                          fontSize: "12px",
                        }}
                      >
                        I wish to subscribe AWS Certification Course (“Course”)
                        through the authorised partner of Avanse. I have
                        verified its usefulness for my personal use and hereby
                        give my consent to subscribe to the said Course. This
                        Course subscription shall form part of my cost of
                        education (COE)
                        <br />
                        <Box sx={{ mt: { xs: 0.5, sm: 1 } }} />
                        I expressly consent to subscribe to the
                        said Course and pay the Course fees (being part of COE);
                        I authorise Avanse to debit my bank account and adjust
                        the proceeds towards subscription cost of the Course.
                      </Box>
                    }
                  />
                  {errors.consent && touched.consent && (
                    <Typography
                      data-testid="consent-error"
                      sx={{
                        color: "#d43a3a",
                        fontSize: 12.5,
                        mt: 0.5,
                        ml: isMobile ? 4 : 5,
                      }}
                    >
                      {errors.consent}
                    </Typography>
                  )}
                </Box>

                {/* Submit */}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    data-testid="submit-button"
                    type="submit"
                    variant="contained"
                    disableElevation
                    disabled={submitting}
                    sx={{
                      bgcolor: TEAL,
                      minWidth: { xs: "100%", sm: 260 },
                      height: 54,
                      borderRadius: 1.5,
                      "&:hover": { bgcolor: TEAL_DARK },
                      "&.Mui-disabled": {
                        bgcolor: TEAL,
                        opacity: 0.7,
                        color: "#fff",
                      },
                      textTransform: "capitalize",
                      color: "#FFFFFF",
                      fontWeight: 500,
                    }}
                    startIcon={
                      submitting ? (
                        <CircularProgress size={18} sx={{ color: "#fff" }} />
                      ) : null
                    }
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </ThemeProvider>
      </DialogContent>
    </Dialog>
  );
}
